import { environment } from "@/config/env";
import axios from "axios";
import { useEffect, useState } from "react";
import { useEliteStructure } from "@/contexts/EliteTemporaryContextProvider";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { motion } from "motion/react";
import {
  Phone,
  Globe,
  MapPin,
  ExternalLink,
  Sparkles,
  HandPlatter,
  MapPinned,
  ArrowRight,
  Hotel,
  Navigation,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ServiceCard from "@/components/ServiceCard";
import PoiCard from "@/components/PoiCard";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { Spinner } from "@/components/ui/spinner";

interface ConciergeTranslations {
  welcomeMessage?: Record<string, string>;
  structureDescription?: Record<string, string>;
}

interface ConciergeMessagePayload {
  translations?: ConciergeTranslations;
}

function normalizeHtmlLineSpacing(value: string) {
  if (!value) return "";

  return value
    .replace(/<p>\s*<\/p>/gi, "<p>&nbsp;</p>")
    .replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, "<p>&nbsp;</p>")
    .replace(/<\/p>\s*<p>/gi, "</p> <p>")
    .replace(/<\/p>\s*<br\s*\/?>/gi, "</p> <br />")
    .replace(/<br\s*\/?>\s*<p>/gi, "<br /> <p>");
}

const hotelRichTextStyles = `
  .hotel-rich-text h1 { font-size: 1.5em; font-weight: 700; margin-bottom: 0.5rem; color: inherit; }
  .hotel-rich-text h2 { font-size: 1.3em; font-weight: 700; margin-bottom: 0.5rem; color: inherit; }
  .hotel-rich-text h3 { font-size: 1.15em; font-weight: 600; margin-bottom: 0.5rem; color: inherit; }
  .hotel-rich-text h4 { font-size: 1em; font-weight: 600; margin-bottom: 0.25rem; color: inherit; }
  .hotel-rich-text p { margin-bottom: 0.75rem; line-height: 1.6; }
  .hotel-rich-text strong { font-weight: 600; }
  .hotel-rich-text ul { list-style-type: disc; margin-left: 1.25rem; margin-bottom: 0.75rem; }
  .hotel-rich-text ol { list-style-type: decimal; margin-left: 1.25rem; margin-bottom: 0.75rem; }
  .hotel-rich-text a { color: var(--primary, #c7261f); text-decoration: underline; }
`;

export default function HotelInfo() {
  const { structure } = useEliteStructure();
  const [messagesData, setMessagesData] =
    useState<ConciergeMessagePayload | null>(null);
  const [welcomeMessage, setWelcomeMessage] = useState("");
  const [structureDescription, setStructureDescription] = useState("");
  const [coverImage, setCoverImage] = useState<string | null>(null);
  const [services, setServices] = useState<any[]>([]);
  const [highlightedPois, setHighlightedPois] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { t, i18n } = useTranslation("hotelInfo");

  useDocumentTitle(structure?.name ? `${structure.name} - Info` : "Informazioni Struttura");

  useEffect(() => {
    if (structure?.id) {
      Promise.allSettled([getMessage(), getPois()]).finally(() => {
        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
  }, [structure?.id]);

  useEffect(() => {
    let highlightedServices = (structure?.services || []).filter(
      (service: any) => service.highlighted,
    );
    setServices(highlightedServices);

    if (
      messagesData &&
      messagesData.translations &&
      messagesData.translations.welcomeMessage
    ) {
      let lang = i18n.language;
      if (lang === "en") lang = "gb";

      let welcome =
        messagesData.translations.welcomeMessage?.[lang] ??
        messagesData.translations.welcomeMessage?.["it"] ??
        "";
      let desc =
        messagesData.translations.structureDescription?.[lang] ??
        messagesData.translations.structureDescription?.["it"] ??
        "";
      setWelcomeMessage(normalizeHtmlLineSpacing(welcome));
      setStructureDescription(normalizeHtmlLineSpacing(desc));
    } else {
      setWelcomeMessage("");
      setStructureDescription("");
    }
  }, [structure?.services, messagesData, i18n.language]);

  async function getPois() {
    axios.defaults.baseURL = environment.ELITE_BASE_URI;
    try {
      const response = await axios.get(`concierge/pointofinterest/${structure.id}`);
      let poiArr = response.data?.poi || [];
      let highlighted = poiArr.filter((item: any) => item.highlighted);
      setHighlightedPois(highlighted);
    } catch (err) {
      console.error("Error fetching POIs for hotel info:", err);
    }
  }

  async function getMessage() {
    axios.defaults.baseURL = environment.ELITE_BASE_URI;
    try {
      const response = await axios.get(`concierge/message/${structure.id}`);
      let parsed: ConciergeMessagePayload | null = null;
      if (
        response.data?.messages &&
        typeof response.data.messages === "string"
      ) {
        try {
          parsed = JSON.parse(response.data.messages);
        } catch (e) {
          console.warn("Impossibile eseguire il parse dei messaggi concierge:", e);
        }
      }
      setMessagesData(parsed);

      const rawCover = response.data?.cover ?? null;
      setCoverImage(
        typeof rawCover === "string" && rawCover.trim()
          ? rawCover.trim()
          : null,
      );
    } catch (err) {
      console.warn("Messaggio concierge non disponibile o errore nel caricamento:", err);
      setMessagesData(null);
      setCoverImage(null);
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-3">
        <Spinner className="size-8 text-primary" />
        <p className="text-sm text-muted-foreground animate-pulse">
          Caricamento informazioni...
        </p>
      </div>
    );
  }

  const mapQueryAddress = [structure?.address, structure?.city]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="w-full space-y-8 pb-12">
      <style>{hotelRichTextStyles}</style>

      {/* Hero Showcase Card */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative overflow-hidden rounded-2xl border bg-card shadow-none"
      >
        {coverImage && (
          <div className="relative aspect-[16/9] sm:aspect-[21/9] md:h-[320px] w-full overflow-hidden border-b">
            <img
              src={coverImage}
              alt={structure?.name || "Hotel Cover"}
              className="size-full object-cover"
              loading="eager"
              onError={() => setCoverImage(null)}
            />
          </div>
        )}

        <div className="p-6 md:p-8 bg-card border-b">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="gap-1.5">
                  <Hotel className="size-3.5" />
                  {t("aboutUs")}
                </Badge>
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight">
                {structure?.name || t("welcome")}
              </h1>
              {mapQueryAddress && (
                <p className="flex items-center gap-1.5 text-sm md:text-base text-muted-foreground mt-1">
                  <MapPin className="size-4 shrink-0 text-primary" />
                  {mapQueryAddress}
                </p>
              )}
            </div>
            {structure?.logo && (
              <img
                src={structure.logo}
                alt={structure?.name}
                className="max-h-14 w-auto object-contain self-start md:self-auto"
              />
            )}
          </div>
        </div>

        {/* Quick Contact & Navigation Shortcuts */}
        <div className="grid grid-cols-1 sm:flex sm:flex-wrap items-center gap-2 p-3 sm:p-4 bg-muted/20">
          {structure?.tel && (
            <Button
              asChild
              variant="outline"
              size="sm"
              className="gap-2 justify-center shadow-none"
            >
              <a href={`tel:${structure.tel}`}>
                <Phone className="size-4 text-primary" />
                <span>{t("callHotel")}</span>
                <span className="text-xs text-muted-foreground hidden lg:inline">
                  ({structure.tel})
                </span>
              </a>
            </Button>
          )}

          {structure?.website && (
            <Button
              asChild
              variant="outline"
              size="sm"
              className="gap-2 justify-center shadow-none"
            >
              <a
                href={structure.website}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Globe className="size-4 text-primary" />
                <span>{t("visitWebsite")}</span>
                <ExternalLink className="size-3 text-muted-foreground ml-auto sm:ml-0" />
              </a>
            </Button>
          )}

          {(structure?.googleMapsPosition || mapQueryAddress) && (
            <Button
              asChild
              variant="outline"
              size="sm"
              className="gap-2 justify-center shadow-none"
            >
              <a
                href={
                  structure?.googleMapsPosition ||
                  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    mapQueryAddress,
                  )}`
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                <Navigation className="size-4 text-primary" />
                <span>{t("openMap")}</span>
                <ExternalLink className="size-3 text-muted-foreground ml-auto sm:ml-0" />
              </a>
            </Button>
          )}
        </div>
      </motion.div>

      {/* Welcome & About Section */}
      {(welcomeMessage || structureDescription) && (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Card className="border shadow-none">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-primary/10 text-primary shrink-0">
                  <Sparkles className="size-5" />
                </div>
                <div>
                  <CardTitle className="text-xl">
                    {t("welcome")}
                  </CardTitle>
                  <CardDescription>
                    {structure?.name || t("aboutUs")}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 pt-1">
              {welcomeMessage && (
                <div
                  className="hotel-rich-text text-base md:text-lg font-medium text-foreground leading-relaxed p-4 rounded-xl bg-muted/40 border border-border/50"
                  style={{ wordWrap: "break-word" }}
                  dangerouslySetInnerHTML={{ __html: welcomeMessage }}
                />
              )}
              {structureDescription && (
                <div
                  className="hotel-rich-text text-sm md:text-base text-muted-foreground leading-relaxed"
                  style={{ wordWrap: "break-word" }}
                  dangerouslySetInnerHTML={{ __html: structureDescription }}
                />
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Highlighted Services Section */}
      {services.length > 0 && (
        <motion.section
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="space-y-4"
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-primary/10 text-primary shrink-0">
                <HandPlatter className="size-5" />
              </div>
              <div>
                <h2 className="text-lg md:text-xl font-bold tracking-tight">
                  {t("highlightedServices")}
                </h2>
              </div>
            </div>
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="gap-1 text-primary hover:text-primary hover:bg-primary/10"
            >
              <Link to="/services">
                <span>{t("allServices")}</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {services.map((service: any, index: number) => (
              <motion.div
                key={service.id || index}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <ServiceCard {...service} />
              </motion.div>
            ))}
          </div>
        </motion.section>
      )}

      {/* Highlighted Points of Interest Section */}
      {highlightedPois.length > 0 && (
        <motion.section
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.25 }}
          className="space-y-4"
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-primary/10 text-primary shrink-0">
                <MapPinned className="size-5" />
              </div>
              <div>
                <h2 className="text-lg md:text-xl font-bold tracking-tight">
                  {t("highlightedPoi")}
                </h2>
              </div>
            </div>
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="gap-1 text-primary hover:text-primary hover:bg-primary/10"
            >
              <Link to="/point-of-interest">
                <span>{t("allPoi")}</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {highlightedPois.map((item: any, index: number) => (
              <motion.div
                key={item.id || index}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <PoiCard poi={item} />
              </motion.div>
            ))}
          </div>
        </motion.section>
      )}

      {/* Location & Map Card */}
      {mapQueryAddress && (
        <motion.section
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Card className="border shadow-none overflow-hidden">
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-primary/10 text-primary shrink-0">
                    <MapPin className="size-5" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">
                      {t("howToReachUs")}
                    </CardTitle>
                    <CardDescription>
                      {mapQueryAddress}
                    </CardDescription>
                  </div>
                </div>
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className="gap-1.5 self-start sm:self-auto shadow-none"
                >
                  <a
                    href={
                      structure?.googleMapsPosition ||
                      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        mapQueryAddress,
                      )}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Navigation className="size-4 text-primary" />
                    <span>{t("openMap")}</span>
                    <ExternalLink className="size-3 text-muted-foreground" />
                  </a>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0 sm:p-6 sm:pt-0">
              <div className="w-full aspect-[16/9] md:h-[380px] overflow-hidden sm:rounded-xl border-y sm:border">
                <iframe
                  title="Google Maps"
                  className="size-full border-0"
                  loading="lazy"
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(
                    mapQueryAddress,
                  )}&z=15&output=embed`}
                />
              </div>
            </CardContent>
          </Card>
        </motion.section>
      )}
    </div>
  );
}
